import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import './ScratchCard.css';

const IMAGE_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || window.location.origin;

const PRIZE_IMAGE_MAP = {
  bouteille_035: 'AC-035.jpg.jpeg',
  bouteille_120: 'AC-120.jpg.jpeg',
  ballon_basket: 'Balle-AC.jpg.jpeg',
  casquette: 'Casquette-Ac.jpg.jpeg',
  gobelet: 'Gobelet-AC.jpg (1).jpeg',
  tenue_basket: 'tenu-basket.jpg (1).jpeg',
  pull: 'pull-AC.jpg.jpeg',
};

const LABEL_IMAGE_MAP = {
  'american cola 0 35l': 'AC-035.jpg.jpeg',
  'american cola 1 20l': 'AC-120.jpg.jpeg',
  'bouteille 0 35l': 'AC-035.jpg.jpeg',
  'bouteille 1 20l': 'AC-120.jpg.jpeg',
  'tenue de basket american cola': 'tenu-basket.jpg (1).jpeg',
  'tenue de basket': 'tenu-basket.jpg (1).jpeg',
  'maillot de basket': 'tenu-basket.jpg (1).jpeg',
  'ballon de basket premium': 'Balle-AC.jpg.jpeg',
  'ballon de basket': 'Balle-AC.jpg.jpeg',
  'casquette american cola': 'Casquette-Ac.jpg.jpeg',
  'casquette': 'Casquette-Ac.jpg.jpeg',
  'gobelet american cola': 'Gobelet-AC.jpg (1).jpeg',
  'gobelet': 'Gobelet-AC.jpg (1).jpeg',
  'pull american cola': 'pull-AC.jpg.jpeg',
  'pull': 'pull-AC.jpg.jpeg',
  'pullover': 'pull-AC.jpg.jpeg',
};

const normalizeKey = (value) => {
  if (!value) return '';
  return value.toString().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim();
};

const normalizeAssetUrl = (value) => {
  if (!value) return null;
  const trimmed = value.toString().trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('data:')) return trimmed;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(trimmed)}`;
};

const resolveImageUrl = (prize) => {
  if (!prize) return null;
  const typeKey = normalizeKey(prize.prize_type).replace(/ /g, '_');
  const direct = PRIZE_IMAGE_MAP[prize.prize_type] || PRIZE_IMAGE_MAP[typeKey];
  const labelKey = normalizeKey(prize.prize_label);
  const nameKey = normalizeKey(prize.name);
  const filename = direct || LABEL_IMAGE_MAP[labelKey] || LABEL_IMAGE_MAP[nameKey];
  if (!filename) return null;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(filename)}`;
};

const isLosingPrize = (prize) => {
  if (!prize?.prize_type) return false;
  const t = prize.prize_type.toLowerCase();
  return t.includes('try_again') || t.includes('reessayer') || t.includes('perdu');
};

const CARD_W = 320;
const CARD_H = 240;

const ScratchCard = ({ prize, onReveal, isRevealed }) => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercentage, setScratchPercentage] = useState(0);
  const [autoRevealed, setAutoRevealed] = useState(false);

  const imageUrl = resolveImageUrl(prize);
  const iconUrl = normalizeAssetUrl(prize?.icon);
  const isLosing = isLosingPrize(prize);

  // Dessiner le design carte.jpg comme surface à gratter
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Couvrir immédiatement avec un fond blanc opaque pour éviter le flickering
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CARD_W, CARD_H);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `${IMAGE_BASE_URL}/${encodeURIComponent('design carte.jpg')}`;

    img.onload = () => {
      ctx.drawImage(img, 0, 0, CARD_W, CARD_H);
    };

    img.onerror = () => {
      // Fallback si l'image ne charge pas : reproduire le design manuellement
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CARD_W, CARD_H);

      // Texte des lots en haut
      ctx.fillStyle = '#bf0a30';
      ctx.font = 'bold 9px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('MAILLOTS DE BASKET – CASQUETTES – REDCUP – PULLOVER', CARD_W / 2, 14);
      ctx.fillText('BALLONS DE BASKET – BOUTEILLES GRATUITES', CARD_W / 2, 26);

      // Zone rouge en bas (vague)
      ctx.fillStyle = '#bf0a30';
      ctx.beginPath();
      ctx.moveTo(0, CARD_H * 0.70);
      ctx.bezierCurveTo(CARD_W * 0.3, CARD_H * 0.62, CARD_W * 0.7, CARD_H * 0.76, CARD_W, CARD_H * 0.68);
      ctx.lineTo(CARD_W, CARD_H);
      ctx.lineTo(0, CARD_H);
      ctx.closePath();
      ctx.fill();

      // "GRATTER ICI !" en blanc gras
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 22px "Arial Black", Arial';
      ctx.textAlign = 'center';
      ctx.fillText('GRATTER ICI !', CARD_W / 2, CARD_H * 0.88);
    };
  }, []);

  const calculateScratchPercentage = (ctx, w, h) => {
    const data = ctx.getImageData(0, 0, w, h).data;
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 128) transparent++;
    }
    return (transparent / (data.length / 4)) * 100;
  };

  const getPos = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();

    const pct = calculateScratchPercentage(ctx, canvas.width, canvas.height);
    setScratchPercentage(pct);

    if (pct > 50 && !autoRevealed) {
      setAutoRevealed(true);
      const ctx2 = canvas.getContext('2d');
      ctx2.clearRect(0, 0, canvas.width, canvas.height);
      setScratchPercentage(100);
      if (!isLosing) {
        setTimeout(() => {
          confetti({
            particleCount: 180,
            spread: 90,
            origin: { y: 0.55 },
            colors: ['#bf0a30', '#ffffff', '#f5c518', '#ff6b6b', '#ffd700'],
            startVelocity: 45,
            gravity: 0.8,
            scalar: 1.1,
          });
        }, 300);
      }
      if (onReveal && !isRevealed) setTimeout(() => onReveal(), 300);
    }
  };

  const handleStart = (e) => {
    if (isRevealed) return;
    e.preventDefault();
    setIsScratching(true);
    const pos = getPos(canvasRef.current, e);
    scratch(pos.x, pos.y);
  };

  const handleMove = (e) => {
    if (!isScratching || isRevealed) return;
    e.preventDefault();
    const pos = getPos(canvasRef.current, e);
    scratch(pos.x, pos.y);
  };

  const handleEnd = () => setIsScratching(false);

  return (
    <div className="scratch-card-container">
      <div className="scratch-card">

        {/* Contenu révélé sous le design */}
        <div className={`scratch-card-content ${isLosing ? 'losing' : 'winning'}`}>
          {isLosing ? (
            <div className="lose-content">
              <div className="lose-icon">😢</div>
              <p className="lose-text">Pas de chance !</p>
              <p className="lose-sub">Retentez avec plus de bouteilles</p>
            </div>
          ) : (
            prize && (
              <>
                <div className="prize-img-wrap">
                  {imageUrl ? (
                    <img src={imageUrl} alt={prize.prize_label || 'Lot'} className="prize-img" />
                  ) : prize.image_url ? (
                    <img src={prize.image_url} alt={prize.prize_label || 'Lot'} className="prize-img" />
                  ) : iconUrl ? (
                    <img src={iconUrl} alt={prize.prize_label || 'Lot'} className="prize-img" />
                  ) : (
                    <span style={{ fontSize: 48 }}>🎁</span>
                  )}
                </div>
                <div className="prize-details">
                  <p className="prize-name-text">{prize.prize_label || prize.name}</p>
                  {prize.prize_code && (
                    <div className="prize-code-box">CODE : {prize.prize_code}</div>
                  )}
                </div>
              </>
            )
          )}
        </div>

        {/* Canvas — surface à gratter (design carte.jpg) */}
        {!isRevealed && (
          <canvas
            ref={canvasRef}
            width={CARD_W}
            height={CARD_H}
            className={`scratch-surface${isScratching ? ' scratching' : ''}`}
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
          />
        )}
      </div>

      {!isRevealed && scratchPercentage > 0 && scratchPercentage < 50 && (
        <div className="scratch-hint">Continuez à gratter...</div>
      )}
    </div>
  );
};

export default ScratchCard;
